import { type ComponentType, useEffect } from 'react';

import { useChartMenuContext } from '../ChartMenuContext';
import { deriveSharedChartType } from './deriveSharedChartType';

export type SharableChartProps<DataT, CPropsT extends object = object> = {
  Component: ComponentType<{ data: DataT } & CPropsT>;
  data: DataT;
} & CPropsT;

/**
 * Wraps a chart component and registers its data with the chart-menu's
 * plot-share (PPT export) state. Shared-report capture is handled separately
 * by the `withShareCapture` HOC on each replots export and does not go
 * through this wrapper.
 */
export function SharableChart<DataT, CPropsT extends object = object>(
  props: SharableChartProps<DataT, CPropsT>
) {
  const { Component, data, ...rest } = props;
  const chartProps = rest as CPropsT;
  const { setShareData } = useChartMenuContext();

  const chartType = deriveSharedChartType(Component);

  useEffect(() => {
    if (!chartType) return;

    setShareData({
      chartType,
      chartProps: chartProps as Record<string, unknown>,
      data,
    });
  }, [chartType, chartProps, data, setShareData]);

  return <Component data={data} {...chartProps} />;
}
